/**
 * "Field profile" section embedded inside the Account-settings page.
 *
 * Renders only for users with the field_rep role.  Lets the rep edit
 * their home address + phone (used later by route view to show
 * distance from home).  Territories are read-only — admins assign
 * those in the Admin → Field tab.
 */

import { Alert, Box, Button, Chip, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchMyRepProfile, updateMyRepProfile, type RepProfile } from '../../api/field';
import { HomeStatePicker } from './StatePickers';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function FieldProfileSection() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['field', 'me'],
    queryFn: fetchMyRepProfile,
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }
  if (query.error) {
    return (
      <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
        {(query.error as Error).message}
      </Alert>
    );
  }
  if (!query.data) return null;

  return (
    <FieldProfileForm
      rep={query.data}
      onSaved={() => queryClient.invalidateQueries({ queryKey: ['field', 'me'] })}
    />
  );
}

function FieldProfileForm({ rep, onSaved }: { rep: RepProfile; onSaved: () => void }) {
  const [homeAddress, setHomeAddress] = useState(rep.home_address ?? '');
  const [homeCity, setHomeCity] = useState(rep.home_city ?? '');
  const [homeState, setHomeState] = useState(rep.home_state ?? '');
  const [homeZip, setHomeZip] = useState(rep.home_zip ?? '');
  const [phone, setPhone] = useState(rep.phone ?? '');

  // Whenever the server data changes (after save), reset form state.
  useEffect(() => {
    setHomeAddress(rep.home_address ?? '');
    setHomeCity(rep.home_city ?? '');
    setHomeState(rep.home_state ?? '');
    setHomeZip(rep.home_zip ?? '');
    setPhone(rep.phone ?? '');
  }, [rep]);

  const mutation = useMutation({
    mutationFn: () =>
      updateMyRepProfile({
        home_address: homeAddress.trim() || null,
        home_city: homeCity.trim() || null,
        home_state: homeState.trim().toUpperCase() || null,
        home_zip: homeZip.trim() || null,
        phone: phone.trim() || null,
      }),
    onSuccess: onSaved,
  });

  const dirty =
    homeAddress !== (rep.home_address ?? '') ||
    homeCity !== (rep.home_city ?? '') ||
    homeState !== (rep.home_state ?? '') ||
    homeZip !== (rep.home_zip ?? '') ||
    phone !== (rep.phone ?? '');

  return (
    <Stack spacing={2}>
      {/* Territories read-only display */}
      <Box>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'rgba(15, 10, 7, 0.55)',
            mb: 0.75,
          }}
        >
          Territory
        </Typography>
        {rep.territory_states.length === 0 ? (
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: 'rgba(15, 10, 7, 0.55)',
              fontStyle: 'italic',
            }}
          >
            Not assigned. An administrator will set your states.
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {rep.territory_states.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                sx={{
                  fontFamily: FONT_INTER,
                  fontWeight: 600,
                  bgcolor: 'rgba(187, 140, 63, 0.08)',
                  color: GOLD,
                  border: `1px solid rgba(187, 140, 63, 0.32)`,
                }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Editable fields */}
      <TextField
        label="Home address"
        value={homeAddress}
        onChange={(e) => setHomeAddress(e.target.value)}
        fullWidth
        size="small"
        inputProps={{ maxLength: 200 }}
      />
      {/* City / State / ZIP — grid layout with proportions tuned to
          the content each field carries: city names can run long, ZIP
          is fixed-width digits, State only needs room for the 2-letter
          code + a chevron.  Stacks on xs so phones don't get crushed. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 120px 140px' },
          gap: 1.5,
        }}
      >
        <TextField
          label="City"
          value={homeCity}
          onChange={(e) => setHomeCity(e.target.value)}
          size="small"
          inputProps={{ maxLength: 100 }}
        />
        <HomeStatePicker value={homeState} onChange={setHomeState} width="100%" />
        <TextField
          label="ZIP"
          value={homeZip}
          onChange={(e) => setHomeZip(e.target.value)}
          size="small"
          inputProps={{ maxLength: 10 }}
        />
      </Box>
      <TextField
        label="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        fullWidth
        size="small"
        inputProps={{ maxLength: 30 }}
      />

      {mutation.error && (
        <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
          {(mutation.error as Error).message}
        </Alert>
      )}
      {mutation.isSuccess && !dirty && (
        <Alert severity="success" sx={{ fontFamily: FONT_INTER }}>
          Saved.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Stack>
  );
}
